import { useState } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import { AlertTriangle, ChevronLeft, Loader2, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import DashboardLayout from '@/frontend/layouts/DashboardLayout'
import FdeShell from '@/frontend/features/Fde/FdeShell'
import ConnectorIcon from '@/frontend/features/Fde/data/ConnectorIcon'
import DataHealthReport from '@/frontend/features/Fde/dataHealth/DataHealthReport'
import OnboardingPanel from '@/frontend/features/Fde/dataHealth/OnboardingPanel'
import type { DataHealthProps, HealthRunSummary } from '@/frontend/features/Fde/dataHealth/types'
import { format, formatDate } from '@/frontend/features/Fde/dataHealth/types'

function runHref(self: string, runId: string) {
  const [path, query = ''] = self.split('?')
  const params = new URLSearchParams(query)
  params.set('run_id', runId)
  return `${path}?${params}`
}

function StatusBanner({ tone, children }: { tone: 'info' | 'warning' | 'error'; children: React.ReactNode }) {
  return (
    <div role={tone === 'error' ? 'alert' : 'status'} className={cn(
      'flex items-start gap-2 rounded-xl border px-4 py-3 text-sm',
      tone === 'info' && 'border-border bg-muted/30',
      tone === 'warning' && 'border-amber-500/40 bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200',
      tone === 'error' && 'border-destructive/40 bg-destructive/5 text-destructive',
    )}>
      {tone === 'info' ? <Loader2 className="mt-0.5 size-4 shrink-0 animate-spin" aria-hidden /> : <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />}
      <span>{children}</span>
    </div>
  )
}

// <option> children must be plain text.
function runLabel(run: HealthRunSummary, copy: Record<string, string>) {
  const trigger = run.trigger ? copy[`run_trigger_${run.trigger}`] : null
  return `${formatDate(run.finished_at ?? run.started_at, true) || run.run_id.slice(0, 8)}${trigger ? `, ${trigger}` : ''}`
}

function DataHealthPage(props: DataHealthProps) {
  const copy = props.health_copy
  const { run, connection, paths } = props
  const [requesting, setRequesting] = useState(false)
  const requestRun = () => {
    setRequesting(true)
    router.post(paths.runs, {}, { preserveScroll: true, onFinish: () => setRequesting(false) })
  }
  const title = format(copy.title, { connection: connection.name })
  const meta = [
    connection.connector_name,
    run?.data_until ? format(copy.data_until, { date: formatDate(run.data_until) }) : null,
    run?.finished_at ? format(copy.checked_at, { date: formatDate(run.finished_at, true) }) : null,
    run?.trigger ? copy[`run_trigger_${run.trigger}`] : null,
  ].filter(Boolean).join(' · ')

  const actions = (
    <Link href={paths.back} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
      <ChevronLeft className="size-3.5" aria-hidden />{copy.back}
    </Link>
  )

  return (
    <>
      <Head title={`${copy.tab}: ${connection.name}`} />
      <FdeShell copy={props.copy} eyebrow="" title={props.project?.name ?? props.organization.name} description="" actions={actions} projectId={props.project?.id}>
        <div className="@container h-full overflow-y-auto bg-muted/10">
          <div className="mx-auto grid max-w-5xl grid-cols-[minmax(0,1fr)] gap-8 px-4 pb-16 pt-6 @3xl:px-6">
            <header className="grid gap-2">
              <div className="text-xs font-semibold uppercase tracking-widest text-primary">{copy.eyebrow}</div>
              <div className="flex items-start gap-3">
                <ConnectorIcon
                  iconUrl={connection.icon_url}
                  className="mt-0.5 size-10 rounded-lg border border-border bg-background text-primary @3xl:size-11"
                  imageClassName="size-7"
                  fallbackClassName="size-5"
                />
                <div className="grid min-w-0 gap-1">
                  <h1 className="m-0 text-2xl font-bold leading-tight tracking-tight [text-wrap:balance] @3xl:text-3xl">{title}</h1>
                  {meta && <p className="m-0 text-sm text-muted-foreground">{meta}</p>}
                </div>
              </div>
              {(props.can_manage || props.runs.length > 1) && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {props.can_manage && (
                    <Button size="sm" variant="outline" onClick={requestRun} disabled={requesting}>
                      <RefreshCw className={cn('size-3.5', requesting && 'animate-spin')} aria-hidden />
                      {requesting ? copy.requesting_run : copy.request_run}
                    </Button>
                  )}
                  {props.runs.length > 1 && (
                    <label className="inline-flex items-center gap-2 text-xs text-muted-foreground">
                      {copy.runs}
                      <select
                        className="h-8 max-w-64 rounded-md border border-border bg-background px-2 text-xs text-foreground"
                        value={props.selected_run_id ?? ''}
                        onChange={(event) => router.visit(runHref(paths.self, event.target.value))}
                      >
                        {props.runs.map((item) => <option key={item.run_id} value={item.run_id}>{runLabel(item, copy)}</option>)}
                      </select>
                    </label>
                  )}
                </div>
              )}
            </header>

            {props.error && <StatusBanner tone="error">{copy.error} {props.error}</StatusBanner>}
            {!props.error && !run && <StatusBanner tone="warning">{copy.no_run}</StatusBanner>}
            {run?.status === 'queued' && <StatusBanner tone="info">{copy.run_queued}</StatusBanner>}
            {run?.status === 'running' && <StatusBanner tone="info">{copy.run_running}</StatusBanner>}
            {run?.status === 'failed' && <StatusBanner tone="error">{copy.run_failed}</StatusBanner>}

            <OnboardingPanel enabled={props.onboarding_enabled} onboarding={props.onboarding} canManage={props.can_manage} approvePath={paths.approve} copy={copy} />

            {run && run.status === 'succeeded' && <DataHealthReport run={run} copy={copy} staff={props.staff} />}
          </div>
        </div>
      </FdeShell>
    </>
  )
}

DataHealthPage.layout = (page: React.ReactElement) => <DashboardLayout variant="fullscreen">{page}</DashboardLayout>
export default DataHealthPage
